import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';
import CreditBalance from '../CreditBalance';
import CreditPurchaseModal from '../CreditPurchaseModal';
import { SubscriptionPlans, CurrentSubscription } from '../subscriptions';
import { Coins, CreditCard, ArrowUpDown, Zap, Package, RefreshCw, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CreditsPageV2 = ({ sessionToken }) => {
  const { currentColors, colorTheme } = useColorTheme();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [balanceKey, setBalanceKey] = useState(0);
  const [activeTab, setActiveTab] = useState('subscription');
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500';
  const gradientBg = colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50';
  const activeBtn = colorTheme === 'purple' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/20' : colorTheme === 'blue' ? 'bg-gradient-to-r from-blue-600 to-sky-500 text-white shadow-lg shadow-blue-500/20' : 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg shadow-emerald-500/20';
  const accentText = colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-400' : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-emerald-600 dark:text-emerald-400';

  useEffect(() => {
    if (sessionToken) {
      fetchTransactions();
      fetchPricingConfig();
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');
      if (sessionId) handlePaymentReturn(sessionId);
    }
  }, [sessionToken]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/api/payments/user/transactions?limit=50`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingConfig = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/credits/pricing-config`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setPricingConfig(response.data);
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    }
  };

  const handlePaymentReturn = async (sessionId) => {
    const statusDiv = document.createElement('div');
    statusDiv.className = 'fixed top-4 right-4 z-50 bg-emerald-50 dark:bg-emerald-900 border border-emerald-200 text-emerald-700 dark:text-emerald-200 px-6 py-4 rounded-2xl shadow-lg backdrop-blur-xl';
    statusDiv.innerHTML = '<div class="flex items-center gap-3"><div class="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div><span class="text-sm font-medium">Verifying payment...</span></div>';
    document.body.appendChild(statusDiv);

    let attempts = 0;
    const pollStatus = async () => {
      if (attempts >= 5) {
        statusDiv.innerHTML = '<span class="text-sm font-medium">Payment verification timed out. Please refresh.</span>';
        setTimeout(() => statusDiv.remove(), 5000);
        return;
      }
      try {
        const response = await axios.get(`${API}/api/payments/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        if (response.data.payment_status === 'paid') {
          statusDiv.innerHTML = '<span class="text-sm font-medium">Payment successful! Credits added.</span>';
          fetchTransactions();
          setBalanceKey(p => p + 1);
          setTimeout(() => { statusDiv.remove(); window.history.replaceState({}, document.title, '/credits'); }, 3000);
        } else if (response.data.status === 'expired') {
          statusDiv.innerHTML = '<span class="text-sm font-medium">Payment session expired.</span>';
          setTimeout(() => statusDiv.remove(), 5000);
        } else { attempts++; setTimeout(pollStatus, 2000); }
      } catch (e) {
        statusDiv.innerHTML = '<span class="text-sm font-medium">Error verifying payment.</span>';
        setTimeout(() => statusDiv.remove(), 5000);
      }
    };
    pollStatus();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase': return <CreditCard className="w-4 h-4" />;
      case 'usage': return <Zap className="w-4 h-4" />;
      case 'refund': return <RefreshCw className="w-4 h-4" />;
      case 'subscription': return <ShieldCheck className="w-4 h-4" />;
      default: return <ArrowUpDown className="w-4 h-4" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'usage': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      case 'refund': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'subscription': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300';
    }
  };

  const tabs = [
    { id: 'subscription', label: 'Subscription Plans', icon: ShieldCheck, badge: 'Save up to 43%' },
    { id: 'one-time', label: 'One-Time Purchase', icon: Package },
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="credits-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>Credits & Billing</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Choose a subscription plan or purchase credits as needed</p>
        </div>

        {/* Balance and Subscription Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm" data-testid="credit-balance-card">
            <CreditBalance
              key={balanceKey}
              onPurchaseClick={() => setShowPurchaseModal(true)}
              minimal={false}
              sessionToken={sessionToken}
            />
          </div>
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm" data-testid="subscription-status-card">
            <CurrentSubscription
              sessionToken={sessionToken}
              onUpdate={setCurrentSubscription}
            />
          </div>
        </div>

        {/* Purchase Tabs */}
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm overflow-hidden mb-8" data-testid="purchase-section">
          <div className="p-1.5 flex gap-1 border-b border-slate-200/50 dark:border-slate-700/50">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-semibold transition-all ${activeTab === tab.id ? activeBtn : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.badge && <span className="ml-1 text-[10px] bg-white/20 px-1.5 py-0.5 rounded-md">{tab.badge}</span>}
                </button>
              );
            })}
          </div>
          <div className="p-6">
            {activeTab === 'subscription' ? (
              <SubscriptionPlans sessionToken={sessionToken} currentSubscription={currentSubscription} />
            ) : (
              <OneTimePurchaseSectionV2
                pricingConfig={pricingConfig}
                onPurchaseClick={() => setShowPurchaseModal(true)}
                colorTheme={colorTheme}
                gradient={gradient}
                accentText={accentText}
              />
            )}
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl shadow-sm overflow-hidden" data-testid="transaction-history">
          <div className="px-5 py-4 border-b border-slate-200/50 dark:border-slate-700/50">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" /> Transaction History
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className={`animate-spin rounded-full h-8 w-8 border-4 border-slate-200 dark:border-slate-700 ${colorTheme === 'purple' ? 'border-t-purple-600' : colorTheme === 'blue' ? 'border-t-blue-600' : 'border-t-emerald-600'}`} />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Coins className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No transactions yet</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Subscribe or purchase credits to see history</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700/50">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Description</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Amount</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Balance</th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/30">
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold ${getTransactionColor(txn.transaction_type)}`}>
                          {getTransactionIcon(txn.transaction_type)}
                          {txn.transaction_type}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="text-sm text-slate-800 dark:text-white">{txn.description}</div>
                        {txn.price_paid_usd && (
                          <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">${txn.price_paid_usd.toFixed(2)} USD</div>
                        )}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`text-sm font-bold ${txn.amount >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                          {txn.amount >= 0 ? '+' : ''}{txn.amount}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-800 dark:text-white">{txn.balance_after}</td>
                      <td className="px-5 py-3 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{formatDate(txn.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <CreditPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onSuccess={() => { setShowPurchaseModal(false); fetchTransactions(); setBalanceKey(p => p + 1); }}
        sessionToken={sessionToken}
      />
    </div>
  );
};

/* One-Time Purchase Section */
const OneTimePurchaseSectionV2 = ({ pricingConfig, onPurchaseClick, colorTheme, gradient, accentText }) => {
  const packages = [
    { name: 'Starter Pack', credits: 95, price: 20, perCredit: 0.21 },
    { name: 'Growth Pack', credits: 290, price: 50, perCredit: 0.17, discount: 17 },
    { name: 'Pro Pack', credits: 680, price: 100, perCredit: 0.15, discount: 29, popular: true },
  ];

  const ringColor = colorTheme === 'purple' ? 'ring-purple-300 dark:ring-purple-700' : colorTheme === 'blue' ? 'ring-blue-300 dark:ring-blue-700' : 'ring-emerald-300 dark:ring-emerald-700';

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-1">One-Time Credit Packages</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">Purchase credits when you need them. No commitment required.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {packages.map((pkg, idx) => (
          <div
            key={idx}
            className={`relative bg-white/80 dark:bg-slate-700/30 rounded-2xl border border-slate-200/50 dark:border-slate-600/50 p-5 transition-all hover:shadow-lg ${pkg.popular ? `ring-2 ${ringColor}` : ''}`}
            data-testid={`credit-package-${idx}`}
          >
            {pkg.popular && (
              <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                <span className={`bg-gradient-to-r ${gradient} text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-sm`}>BEST VALUE</span>
              </div>
            )}
            <div className="text-center pt-2">
              <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-2">{pkg.name}</h4>
              <div className="text-3xl font-bold text-slate-900 dark:text-white">${pkg.price}</div>
              <div className="text-xs text-slate-400 dark:text-slate-500 mb-3">one-time</div>
              <div className="bg-slate-50/80 dark:bg-slate-600/30 rounded-xl p-3 mb-3">
                <div className={`text-2xl font-bold ${accentText}`}>{pkg.credits}</div>
                <div className="text-xs text-slate-600 dark:text-slate-300">credits</div>
                <div className="text-[10px] text-slate-400 mt-0.5">${pkg.perCredit.toFixed(2)}/credit</div>
              </div>
              {pkg.discount && <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Save {pkg.discount}%</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center mt-4">
        <button
          onClick={onPurchaseClick}
          className={`px-8 py-3 rounded-xl font-semibold text-white text-sm transition-all shadow-lg bg-gradient-to-r ${gradient} hover:shadow-xl hover:-translate-y-0.5`}
          data-testid="purchase-credits-btn"
        >
          <span className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> Purchase Credits</span>
        </button>
      </div>

      {pricingConfig && (
        <div className="mt-6 bg-slate-50/80 dark:bg-slate-700/30 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
            <Coins className="w-4 h-4" /> Credit Costs
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Text Ad', value: pricingConfig.text_ad_credits },
              { label: 'Image Ad', value: pricingConfig.image_ad_credits },
              { label: 'Video Ad', value: pricingConfig.video_ad_credits },
              { label: 'Full Campaign', value: pricingConfig.complete_campaign_credits },
            ].map((item, i) => (
              <div key={i} className="text-center p-3 bg-white/60 dark:bg-slate-600/20 rounded-xl">
                <div className={`text-xl font-bold ${accentText}`}>{item.value}</div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsPageV2;
